import Head from 'next/head'
import qs from 'qs'
import { AlgoliaIndex, SearchFiltersRefinement } from 'search'
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { NextPageContext, GetServerSideProps } from 'next'
import { SearchResults, SearchState } from 'react-instantsearch-core'
import { get, isNil } from 'lodash/fp'
import { logSpecificPageView } from 'mParticle/mParticleService'
import { useEffect } from 'react'

import ErrorPage from 'pages/_error'
import Grid, { GridConfig } from 'shared/components/Grid'
import PageContextProvider from 'shared/contexts/PageContextProvider'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import Spinner from 'shared/components/Spinner'
import createApolloClient from 'shared/apollo/createApolloClient'
import getBugsnagClient from 'shared/lib/getBugsnagClient'
import { canonicalSlugMap } from 'head/utils/seoHelpers'
import { getResultsState, urlToSearchState } from 'shared/utils/algolia-utils'
import { isomorphicLog } from 'shared/utils/logging-utils'
import { t, serverSideConfigs } from 'localization'
import { trackSnapchatPageView } from 'tracking/snapchatService'
import { useRouter } from 'next/router'

const TP = 'pages.collections.slug'

export const GET_COLLECTION = gql`
  query getCollection($slug: String!) {
    getCollection(slug: $slug) {
      id
      slug
      title
      description
      indexName
    }
  }
`
interface Collection {
  title: string
  indexName: string
  description: string
}

interface SuccessProps {
  slug: string
  resultsState: SearchResults
  initialSearchState: SearchState
  trendingResultsState: SearchResults | null
  loading: boolean
  collection: Collection
}

interface ErrorProps {
  error: unknown
  errorStatus: number
}

type CollectionPageProps = SuccessProps | ErrorProps

interface Context extends NextPageContext {
  query: {
    slug: string
  }
  apolloClient: ApolloClient<{}> // find a better way to do this
}

const isErrorProps = (props: CollectionPageProps): props is ErrorProps => {
  return (props as ErrorProps).error !== undefined
}

const CollectionPage = (props: CollectionPageProps) => {
  if (isErrorProps(props)) {
    const { error, errorStatus } = props
    if (error) {
      return <ErrorPage statusCode={errorStatus} />
    }
    return null
  }

  const {
    slug,
    resultsState,
    initialSearchState,
    trendingResultsState,
    collection,
    loading,
  } = props
  if (loading) {
    return <Spinner showSpinner={loading} threshold={1000} />
  }

  const index = get('indexName', collection) || AlgoliaIndex.Collection
  const router = useRouter()

  useEffect(() => {
    logSpecificPageView({
      slug,
      pageName: SpecificPageTrackingEvents.CollectionPage,
      slugType: 'collection',
    })
    trackSnapchatPageView()
  }, [])

  const hiddenFilters: Set<string> = new Set()
  // currently presentation sizes are only used for apparel
  hiddenFilters.add(SearchFiltersRefinement.PresentationSizes)
  const config: GridConfig = {
    context: {
      hiddenFilters,
      hasNewProductOnly: false,
      // filter syntax per algolia:
      // https://www.algolia.com/doc/api-reference/api-parameters/filters/
      filters: `collection_slugs:${slug}`,
      index,
    },
    /**
     * regex looks for /collections and grabs everything after collections
     */
    regex: new RegExp(/(\/collections)(.*)/g),
    indexPath: '/collections',
  }

  return (
    <>
      <PageContextProvider value={SpecificPageTrackingEvents.CollectionPage}>
        <Head>
          <title>
            {t(`${TP}.pageTitle`, {
              defaultValue: '{title} | Flight Club',
              title: collection?.title,
            })}
          </title>
          {canonicalSlugMap.has(slug) && (
            <link
              key="canonical"
              rel="canonical"
              href={`https://www.flightclub.com${canonicalSlugMap.get(slug)}${router?.query?.page &&
                `?page=${router?.query?.page}`}`}
            />
          )}
        </Head>
        <Grid
          title={collection?.title}
          description={collection?.description}
          resultsState={resultsState}
          config={config}
          initialSearchState={initialSearchState}
          noResultsState={trendingResultsState}
        />
      </PageContextProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  res,
  req,
  query: { slug },
  locale,
}) => {
  const bugsnagClient = getBugsnagClient()
  const asPath = req.url || ''
  const apolloClient: ApolloClient<NormalizedCacheObject> = createApolloClient(
    {},
    {
      getCsrf: () => null,
      getToken: () => null,
      req,
      res,
    },
  )
  bugsnagClient.leaveBreadcrumb('CollectionPage', {
    slug,
  })

  try {
    const { data, loading } = await apolloClient.query({
      query: GET_COLLECTION,
      variables: {
        slug,
      },
    })

    const collection = get('getCollection', data)
    const index = get('indexName', collection) || AlgoliaIndex.Collection
    const searchState: SearchState = {
      refinementList: {
        collection_slugs: [slug],
      },
      indices: {
        [index]: {
          configure: {
            hitsPerPage: 30,
          },
        },
      },
    }

    const initialSearchState: SearchState = urlToSearchState(
      asPath.includes('?') ? qs.parse(asPath.substring(asPath.indexOf('?') + 1)) : {},
    )

    const { resultsState, trendingResultsState } = await getResultsState(
      initialSearchState,
      searchState,
    )

    return JSON.parse(
      JSON.stringify({
        props: {
          ...(await serverSideConfigs(res, locale)),
          resultsState,
          initialSearchState,
          slug,
          trendingResultsState,
          collection,
          loading,
        },
      }),
    )
  } catch (error) {
    const graphqlStatus = get('graphQLErrors[0].extensions.response.status')(error)
    const status = graphqlStatus || 500

    if (!!graphqlStatus) {
      isomorphicLog(
        `Status returned ${graphqlStatus} when rendering collection ${slug} page: ${JSON.stringify(
          error,
        )}`,
      )
    }

    if (status >= 400 && status <= 599 && !isNil(res)) {
      res.statusCode = status
    }

    return {
      props: {
        ...(await serverSideConfigs(res, locale)),
        error,
        errorStatus: status,
      },
    }
  }
}

export default CollectionPage
